import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  IconButton,
  Chip,
  Stack,
  Divider,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  ShoppingCart as ShoppingCartIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { AuthContext } from "context/Authentication";
import {
  postRecord,
  handleApiResponse,
  handleApiError,
} from "services/services";

// API endpoints
const API_GetAddonsByProductId = "product-addons/getbyproductid-enhanced";
const API_AddToCart = "cart/create";
const API_UpdateCart = "cart/update";
const API_GetCartByUserId = "cart/getbyuserid";

export default function ProductAddonsSelector({
  open,
  onClose,
  product,
  selectedCombination,
  units,
  onAddToCartComplete,
  mainProductPrice,
  selectedChoices = {},
}) {
  const context = useContext(AuthContext);

  // State management
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState({});
  const [step, setStep] = useState(1); // 1: Select addons, 2: Review & confirm

  // Load available addons when dialog opens
  useEffect(() => {
    if (open && product && selectedCombination) {
      loadAvailableAddons();
    }
  }, [open, product, selectedCombination]);

  const loadAvailableAddons = async () => {
    try {
      setLoading(true);
      const response = await postRecord(API_GetAddonsByProductId, {
        Product_Id: product.Product_Id,
        Combination_Id: selectedCombination.Combination_Id,
      });

      if (response.status === 200 && response.data) {
        setAvailableAddons(response.data);

        // Auto-select required addons
        const requiredSelections = {};
        response.data.forEach((addon) => {
          if (addon.Is_Required === "1") {
            requiredSelections[addon.Product_Addon_Id] = {
              selected: true,
              addon: addon,
              quantity: 1,
            };
          }
        });
        setSelectedAddons(requiredSelections);
      } else {
        setAvailableAddons([]);
      }
    } catch (error) {
      console.log("No addons found for this combination");
      setAvailableAddons([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddonToggle = (addon) => {
    // Don't allow deselecting required addons
    if (addon.Is_Required === "1" && selectedAddons[addon.Product_Addon_Id]) {
      return;
    }

    setSelectedAddons((prev) => {
      const newSelections = { ...prev };

      if (newSelections[addon.Product_Addon_Id]) {
        delete newSelections[addon.Product_Addon_Id];
      } else {
        newSelections[addon.Product_Addon_Id] = {
          selected: true,
          addon: addon,
          quantity: 1,
        };
      }

      return newSelections;
    });
  };

  const calculateTotal = () => {
    const mainTotal = mainProductPrice * units;
    const addonsTotal = Object.values(selectedAddons).reduce((total, item) => {
      const price =
        item.addon.Addon_Inventory?.Total_Selling_Price ||
        item.addon.Addon_Inventory?.Price_Per_Unit ||
        0;
      return total + price * item.quantity * units;
    }, 0);

    return mainTotal + addonsTotal;
  };

  const getSelectedAddonsList = () => {
    return Object.values(selectedAddons).map((item) => item.addon);
  };

  const validateRequiredAddons = () => {
    const requiredAddons = availableAddons.filter(
      (addon) => addon.Is_Required === "1"
    );
    const selectedAddonIds = Object.keys(selectedAddons);

    return requiredAddons.every((addon) =>
      selectedAddonIds.includes(addon.Product_Addon_Id.toString())
    );
  };

  const handleProceedToReview = () => {
    if (!validateRequiredAddons()) {
      // Show error for missing required addons
      return;
    }
    setStep(2);
  };

  const handleConfirmAndAddToCart = async () => {
    setProcessing(true);

    try {
      // Add main product to cart
      await addProductToCart(
        product,
        selectedCombination,
        units,
        mainProductPrice
      );

      // Add selected addons to cart
      for (const selection of Object.values(selectedAddons)) {
        const addon = selection.addon;
        const addonPrice =
          addon.Addon_Inventory?.Total_Selling_Price ||
          addon.Addon_Inventory?.Price_Per_Unit ||
          0;

        // Create addon product object for cart
        const addonProduct = {
          Product_Id: addon.Addon_Product_Id,
          Product_Full_Name: addon.Addon_Product_Name,
          Product_Code: addon.Addon_Product_Code,
        };

        const addonCombination = {
          Combination_Id: addon.Addon_Combination_Id,
          Combination_Text: addon.Addon_Combination_Text,
        };

        await addProductToCart(
          addonProduct,
          addonCombination,
          selection.quantity * units,
          addonPrice
        );
      }

      // Success callback
      if (onAddToCartComplete) {
        onAddToCartComplete(getSelectedAddonsList());
      }

      onClose();
    } catch (error) {
      console.error("Error adding products to cart:", error);
    } finally {
      setProcessing(false);
    }
  };

  const addProductToCart = async (
    productData,
    combinationData,
    quantity,
    price
  ) => {
    try {
      // Check if item already exists in cart
      const cartResponse = await postRecord(API_GetCartByUserId, {
        User_Id: context.state.user.User_Id,
      });

      let existingCartItem = null;
      if (cartResponse.status === 200 && cartResponse.data) {
        existingCartItem = cartResponse.data.find(
          (item) => item.Combination_Id === combinationData.Combination_Id
        );
      }

      if (existingCartItem) {
        // Update existing cart item
        const updatedCartItem = {
          ...existingCartItem,
          Units: existingCartItem.Units + quantity,
          Total_Price:
            (existingCartItem.Units + quantity) *
            existingCartItem.Price_Per_Unit,
          Modified_By: context.state.user.User_Id,
        };

        await postRecord(API_UpdateCart, updatedCartItem);
      } else {
        // Create new cart item
        const newCartItem = {
          User_Id: context.state.user.User_Id,
          Product_Id: productData.Product_Id,
          Combination_Id: combinationData.Combination_Id,
          Units: quantity,
          Price_Per_Unit: price,
          Total_Price: quantity * price,
          Is_Savefor_Later: "0",
          Added_By: context.state.user.User_Id,
        };

        await postRecord(API_AddToCart, newCartItem);
      }
    } catch (error) {
      throw error;
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedAddons({});
    onClose();
  };

  if (!product || !selectedCombination) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "70vh",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight="bold" color="primary">
              {step === 1 ? "Choose Your Add-ons" : "Review Your Selection"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {step === 1
                ? "Enhance your product with these amazing add-ons"
                : "Confirm your selection before adding to cart"}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
          >
            <CircularProgress size={60} />
          </Box>
        ) : availableAddons.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary">
              No add-ons available for this product
            </Typography>
          </Box>
        ) : step === 1 ? (
          // Step 1: Addon Selection
          <Grid container spacing={3}>
            {availableAddons.map((addon) => {
              const isSelected = !!selectedAddons[addon.Product_Addon_Id];
              const isRequired = addon.Is_Required === "1";
              const price =
                addon.Addon_Inventory?.Total_Selling_Price ||
                addon.Addon_Inventory?.Price_Per_Unit ||
                0;

              return (
                <Grid item xs={12} sm={6} md={4} key={addon.Product_Addon_Id}>
                  <Card
                    sx={{
                      cursor: isRequired ? "default" : "pointer",
                      borderRadius: 3,
                      transition: "all 0.3s ease",
                      border: isSelected ? 2 : 1,
                      borderColor: isSelected ? "primary.main" : "divider",
                      transform: isSelected ? "scale(1.02)" : "scale(1)",
                      boxShadow: isSelected ? 3 : 1,
                      "&:hover": !isRequired && {
                        transform: "scale(1.02)",
                        boxShadow: 3,
                      },
                    }}
                    onClick={() => handleAddonToggle(addon)}
                  >
                    <Box position="relative">
                      {addon.Addon_Media && addon.Addon_Media.length > 0 && (
                        <CardMedia
                          component="img"
                          height="160"
                          image={`${process.env.BASEURL}${addon.Addon_Media[0].Media_File_Path}`}
                          alt={addon.Addon_Product_Name}
                          sx={{ objectFit: "cover" }}
                        />
                      )}

                      {/* Selection indicator */}
                      {isSelected && (
                        <Box
                          position="absolute"
                          top={8}
                          right={8}
                          sx={{
                            backgroundColor: "primary.main",
                            borderRadius: "50%",
                            p: 0.5,
                          }}
                        >
                          <CheckCircleIcon
                            sx={{ color: "white", fontSize: 20 }}
                          />
                        </Box>
                      )}

                      {/* Required badge */}
                      {isRequired && (
                        <Chip
                          label="Required"
                          size="small"
                          color="error"
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            fontWeight: "bold",
                          }}
                        />
                      )}
                    </Box>

                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {addon.Addon_Product_Name}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {addon.Addon_Combination_Text}
                      </Typography>

                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={2}
                      >
                        <Typography
                          variant="h6"
                          color="primary"
                          fontWeight="bold"
                        >
                          <small style={{ fontSize: "0.8em" }}>
                            {context.state?.companyInfo?.Currency}
                          </small>
                          {price.toFixed(2)}
                        </Typography>

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isSelected}
                              disabled={isRequired}
                              color="primary"
                            />
                          }
                          label=""
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          // Step 2: Review Selection
          <Box>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Main Product
              </Typography>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    {product.Product_Full_Name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCombination.Combination_Text} × {units}
                  </Typography>
                </Box>
                <Typography variant="h6" color="primary">
                  <small>{context.state?.companyInfo?.Currency}</small>
                  {(mainProductPrice * units).toFixed(2)}
                </Typography>
              </Box>
            </Paper>

            {/* Selected Choice Options */}
            {selectedChoices && Object.keys(selectedChoices).length > 0 && (
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Selected Options
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {Object.entries(selectedChoices).map(([category, value]) => (
                    <Chip
                      key={category}
                      label={`${category}: ${value}`}
                      color="primary"
                      variant="outlined"
                      sx={{
                        fontWeight: "medium",
                        fontSize: "0.875rem",
                      }}
                    />
                  ))}
                </Stack>
              </Paper>
            )}

            {Object.keys(selectedAddons).length > 0 && (
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Selected Add-ons
                </Typography>
                <Stack spacing={2}>
                  {Object.values(selectedAddons).map((selection) => {
                    const addon = selection.addon;
                    const price =
                      addon.Addon_Inventory?.Total_Selling_Price ||
                      addon.Addon_Inventory?.Price_Per_Unit ||
                      0;

                    return (
                      <Box
                        key={addon.Product_Addon_Id}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {addon.Addon_Product_Name}
                            {addon.Is_Required === "1" && (
                              <Chip
                                label="Required"
                                size="small"
                                color="error"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {addon.Addon_Combination_Text} ×{" "}
                            {selection.quantity * units}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body1"
                          color="primary"
                          fontWeight="bold"
                        >
                          <small>{context.state?.companyInfo?.Currency}</small>
                          {(price * selection.quantity * units).toFixed(2)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Paper>
            )}

            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? theme.palette.primary.dark
                    : theme.palette.primary.light,
                border: (theme) =>
                  theme.palette.mode === "dark"
                    ? `1px solid ${theme.palette.primary.main}`
                    : "none",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.contrastText
                        : "inherit",
                  }}
                >
                  Total Amount
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.contrastText
                        : theme.palette.primary.main,
                  }}
                >
                  <small>{context.state?.companyInfo?.Currency}</small>
                  {calculateTotal().toFixed(2)}
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        {step === 1 ? (
          <Box display="flex" justifyContent="space-between" width="100%">
            <Button onClick={handleClose} size="large">
              Skip Add-ons
            </Button>
            <Button
              variant="contained"
              onClick={handleProceedToReview}
              size="large"
              startIcon={<ShoppingCartIcon />}
              disabled={!validateRequiredAddons()}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontSize: "1.1rem",
              }}
            >
              Continue ({Object.keys(selectedAddons).length} selected)
            </Button>
          </Box>
        ) : (
          <Box display="flex" justifyContent="space-between" width="100%">
            <Button onClick={() => setStep(1)} size="large">
              Back to Selection
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmAndAddToCart}
              size="large"
              startIcon={
                processing ? <CircularProgress size={20} /> : <AddIcon />
              }
              disabled={processing}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontSize: "1.1rem",
              }}
            >
              {processing ? "Adding to Cart..." : "Add to Cart"}
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
}
